import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { InitDisplayedCol, Page, PageCtrl } from '../../models/common';
import { PermissionService } from 'src/app/utils/permission.service';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-table5-ui',
  templateUrl: './table5-ui.component.html',
  styleUrls: ['./table5-ui.component.css'],
})
export class Table5UiComponent implements OnInit {
  @ViewChild('table') table: MatTable<any>;
  @ViewChild(MatSort) sort: MatSort;
  @Input() set data(payload: any) {
    let data = payload;
    if (payload) {
      data = this.preCheckSelectedIds(payload);
    }
    this.dataSource = new MatTableDataSource(
      Array.isArray(payload) ? payload : null
    );
  }
  @Input() initColumns: InitDisplayedCol[];
  @Input() selectedIds: Set<number> = new Set();
  @Input() pageProp: Page;
  @Input() showPagination: boolean;

  @Input() usePermission: boolean = false;

  @Input() matSortActive: string = '';
  @Input() matSortDirection: SortDirection = '';
  @Input() showCheckboxes: boolean = true;

  @Output() checkboxCheckEmit: EventEmitter<any> = new EventEmitter();
  @Output() pageSizeToggle: EventEmitter<number> = new EventEmitter();
  @Output() PageCtrlToggle: EventEmitter<PageCtrl> = new EventEmitter();
  @Output() sortEmit: EventEmitter<any> = new EventEmitter();
  @Output() rowClick: EventEmitter<any> = new EventEmitter();

  public headers: {
    value: string;
    count: number;
    colDef: string;
    colDefIndex: number;
    sticky?: boolean;
  }[] = [];

  public columnGroupMeta: Record<
    string,
    { isStart: boolean; isEnd: boolean; hasLabel: boolean; isSolo: boolean }
  > = {};

  public displayedColumns: string[];
  public visibleColumns: string[];
  public dataSource: MatTableDataSource<any>;
  public masterCheckboxChecked: boolean;
  public headersCol = [];
  // Columns rendered in the leaf (second) header row. Only columns that belong
  // to a labeled group appear here — ungrouped "solo" columns are rendered in
  // the band row with rowspan=2 so their label fills the full header height.
  public leafColumns: string[] = [];
  // True when at least one column declares a group `header`. When false the
  // table renders a single, plain header row (no band row, no rowspan).
  public hasGroupedHeaders = false;

  minDate: Date;
  maxDate: Date;

  constructor(private permissionsService: PermissionService) {
    const currentYear = new Date().getFullYear();
    const currentTime = new Date();
    this.minDate = new Date(currentYear - 6, 0, 1);
    this.maxDate = new Date(currentTime);
  }

  isColumnVisible(columnKey: string): boolean {
    return this.visibleColumns.includes(columnKey);
  }

  private updateVisibleColumns() {
    this.visibleColumns = this.initColumns.map((c) => c.key);
    this.displayedColumns = this.visibleColumns;
  }

  private checkAndSetHeaders() {
    this.headers = [];
    this.headersCol = [];
    this.leafColumns = [];
    this.columnGroupMeta = {};

    // Only build a grouped (two-row) header when at least one column declares a
    // group label. Otherwise fall back to a single plain header row.
    this.hasGroupedHeaders = this.initColumns.some(
      (c) => (c.header || '') !== ''
    );

    if (!this.hasGroupedHeaders) {
      this.leafColumns = this.initColumns.map((c) => c.key);
      return;
    }

    let index = 0;
    while (index < this.initColumns.length) {
      const current = this.initColumns[index];
      const headerLabel = current.header || '';

      if (headerLabel === '') {
        // Ungrouped column: it lives in the LEAF row alongside the grouped leaf
        // headers so every column label shares one baseline. A blank WHITE spacer
        // cell holds its place in the band row, signalling "not part of a group".
        const spacerDef = `__spacer__${current.key}`;
        this.headers.push({
          value: '',
          count: 1,
          colDef: spacerDef,
          colDefIndex: index,
          // Keep the band-row spacer in sync with its sticky leaf column so the
          // header stays aligned when scrolling horizontally.
          sticky: !!current.sticky,
        });
        this.headersCol.push(spacerDef);
        this.leafColumns.push(current.key);
        this.columnGroupMeta[current.key] = {
          // No side borders: consecutive ungrouped columns merge into one
          // continuous gray band. The white gap before the first labeled group
          // is supplied by that group's own tbl-grp-start border.
          isStart: false,
          isEnd: false,
          hasLabel: false,
          isSolo: false,
        };
        index += 1;
        continue;
      }

      // Labeled group: span consecutive columns sharing the same header.
      let count = 1;
      for (
        let j = index + 1;
        j < this.initColumns.length &&
        (this.initColumns[j].header || '') === headerLabel;
        j++
      ) {
        count++;
      }

      this.headers.push({
        value: headerLabel,
        count: count,
        colDef: headerLabel,
        colDefIndex: index,
      });
      this.headersCol.push(headerLabel);

      const end = index + count - 1;
      for (let k = index; k <= end; k++) {
        const col = this.initColumns[k];
        this.leafColumns.push(col.key);
        this.columnGroupMeta[col.key] = {
          isStart: k === index,
          isEnd: k === end,
          hasLabel: true,
          isSolo: false,
        };
      }

      index += count;
    }
  }

  ngOnInit(): void {
    this.checkAndSetHeaders();
    this.updateVisibleColumns();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.checkAndSetHeaders();
    this.updateVisibleColumns();
    if (!this.selectedIds.size) {
      this.masterCheckboxChecked = false;
    }
  }

  ngAfterViewInit() {
    if (this.dataSource) this.dataSource.sort = this.sort;
  }

  private preCheckSelectedIds(data: any[]) {
    if (!data || !data.length) return;
    return data.map((d) => {
      d.checked = this.selectedIds.has(d.id);
      return d;
    });
  }

  public hasPermission(perms: string[]) {
    return this.permissionsService.hasPermissionList(perms);
  }

  public showToolTip(val, maxLen) {
    const len = maxLen ?? 50;
    if (val.length <= len) {
      return val;
    }
    return '';
  }

  public announceSortChange(sortState: any) {
    this.sortEmit.emit(sortState);
  }

  public onPageSizeToggle(val: number) {
    this.pageSizeToggle.emit(val);
  }

  public onPageCtrlToggle(val: PageCtrl) {
    this.PageCtrlToggle.emit(val);
  }

  public onCheck(event: MatCheckboxChange, item: any) {
    if (this.masterCheckboxChecked) this.masterCheckboxChecked = false;
    const elemCount = this.dataSource.data.length;
    this.dataSource.data.map((d: any) => {
      if (d.id) {
        if (d.id === item.id) d.checked = event.checked;
        if (d.checked) {
          this.selectedIds.add(d.id);
        } else {
          this.selectedIds.delete(d.id);
        }
        return;
      }

      if (d.accountId) {
        if (d.accountId === item.accountId) d.checked = event.checked;
        if (d.checked) {
          this.selectedIds.add(d.accountId);
        } else {
          this.selectedIds.delete(d.accountId);
        }
        return;
      }
    });
    if (this.selectedIds.size === elemCount) this.masterCheckboxChecked = true;
    this.checkboxCheckEmit.emit(this.selectedIds);
  }

  public onCheckAll(event: MatCheckboxChange) {
    this.masterCheckboxChecked = event.checked;
    this.dataSource.data.forEach((element: any) => {
      element.checked = event.checked;
    });
    this.selectedIds = new Set(
      this.dataSource.data.map((val) => val.id || val.accountId)
    );
    event.checked
      ? this.checkboxCheckEmit.emit(this.selectedIds)
      : this.checkboxCheckEmit.emit(new Set());
  }

  public unCheckAll() {
    this.masterCheckboxChecked = false;
    this.data.forEach((element: any) => {
      element.checked = false;
    });
  }

  public triggerRowClick(rowId: number) {
    this.rowClick.emit(rowId);
  }
}
